/* ============================================================
   Self-serve signup — shared between PublicOnboarding (collects the
   plan + org details and fires the Firebase email link) and
   PublicActivate (sets the password and provisions the org + admin).

   No server: the org + fleet-admin user docs are written client-side
   AFTER the user proves email ownership via the sign-in link and sets
   a password. Payments are mocked — a self-serve org starts on a live
   pilot. Real money (Stripe / M-PESA) is a later pass.
   ============================================================ */
import { User } from 'firebase/auth';
import { doc, deleteDoc, getDoc, setDoc, updateDoc, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { PaidPlanId, BillingPeriod, PILOT_DAYS, PILOT_BILL_DELAY_DAYS } from './subscriptionModel';
import { EducationSystem } from '../utils/educationSystems';

export type AddonId = 'gps' | 'fuel' | 'fees';
export type AddonStatus = 'default' | 'connected' | 'off';
export interface AddonState {
  wanted: boolean;
  status: AddonStatus;
}
export type Addons = Record<AddonId, AddonState>;

export type SelfServeProduct = 'bus' | 'lorry' | 'class' | 'family';

export interface SelfServePayload {
  product: SelfServeProduct;
  lorryMode: 'personal' | 'cargo';
  code: string; // full org code incl. suffix (e.g. GREENVALLEY.bus)
  orgName: string;
  contactName: string;
  email: string;
  phone: string;
  size: number; // children (school/bus) or vehicles (lorry)
  fleetCount: number; // buses / drivers
  parents: number;
  /** The plan the account lands on — for a pilot, what it becomes when the pilot
   *  ends. Already includes any Setup upgrade the buyer switched on (see
   *  effectivePlan in subscriptionModel). */
  plan: PaidPlanId;
  period: BillingPeriod;
  intent: 'trial' | 'plan';
  addons: Addons;
  /** Setup upgrades the buyer asked for. `plan` above is already raised to match;
   *  these are kept so provisioning knows WHY (e.g. seed branch support) rather
   *  than having to infer intent from the plan alone. */
  zanaMode?: boolean;
  multiBranch?: boolean;
  /** Curriculum the school runs — drives the year-group picker at parent signup
   *  and the year-end promotion ladder. Only meaningful for school products. */
  educationSystem?: EducationSystem;
  /** School term calendar picked at onboarding: termCalendar[i] = 0 (break) | 1|2|3
   *  (which term month i belongs to). Persisted on the org so billing can charge on
   *  real term boundaries. School products only. */
  termCalendar?: number[];
  /** First day of the current term (YYYY-MM-DD). Required for school signup.
   *  Fuel and route charts use this; billing still uses termCalendar months. */
  currentTermStartDate?: string;
  currentTermEndDate?: string | null;
  /** The org's home location (school/depot gate) placed at signup. A user-declared
   *  coordinate we may store forever — drives geofencing. Required for new orgs; the
   *  org doc's locationSetupPending mirrors !homeLocations. */
  homeLocations?: { lat: number; lng: number } | null;
}

const EMAIL_KEY = 'pear_signup_email';
const PAYLOAD_KEY = 'pear_signup_payload';

/**
 * Encode a signup payload into a compact base64url token to carry INSIDE the activation
 * link. This is what makes cross-browser / cross-account signup bulletproof: the plan +
 * org details travel with the link itself, so /activate needs no localStorage and no
 * Firestore read (immune to cleared storage, fresh browsers, and ad-blockers that block
 * Firestore). The token is not secret — it's the user's own signup data, already in their
 * inbox — and provisioning is still gated by the email-link-verified identity.
 */
export function encodePayloadToken(payload: SelfServePayload): string {
  const json = JSON.stringify(payload);
  // encodeURIComponent→unescape lets btoa handle non-ASCII org names without throwing.
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // → base64url, no padding
}

export function decodePayloadToken(token: string): SelfServePayload | null {
  try {
    let b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = decodeURIComponent(escape(atob(b64)));
    const p = JSON.parse(json) as SelfServePayload;
    // Shape guard — a corrupted/truncated token returns null and falls through to other tiers.
    if (p && typeof p.code === 'string' && p.code && typeof p.email === 'string' && p.email) return p;
    return null;
  } catch {
    return null;
  }
}

/** Map a payload's product (+ lorry mode) to the org doc productType the rest of the app routes on. */
export function productTypeFor(p: Pick<SelfServePayload, 'product' | 'lorryMode'>): string {
  if (p.product === 'family') return 'FAMILIES';
  if (p.product === 'lorry') return p.lorryMode === 'cargo' ? 'CARGO' : 'SELF_TRACKING';
  if (p.product === 'class') return 'FULL_FEATURE';
  return 'BUS_ONLY';
}

/** Default add-on map from the onboarding setup toggles. status starts 'default' (not yet connected). */
export function addonsFromSetup(setup: { gps?: boolean; fuel?: boolean; fees?: boolean }): Addons {
  const mk = (wanted: boolean): AddonState => ({ wanted: !!wanted, status: wanted ? 'default' : 'off' });
  return { gps: mk(setup.gps ?? false), fuel: mk(setup.fuel ?? false), fees: mk(setup.fees ?? false) };
}

/** Firebase email-link settings — the link returns the user to /activate to finish signup.
 *  When a payload is supplied, it's encoded into the link (?d=token) so the signup carries
 *  across browsers/accounts with no storage or Firestore dependency. */
export function activateActionSettings(payload?: SelfServePayload) {
  const origin = process.env.REACT_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const base = `${origin}/activate`;
  const url = payload ? `${base}?d=${encodePayloadToken(payload)}` : base;
  return { url, handleCodeInApp: true };
}

/** Firebase email-link settings for admin invites — link goes to /activate with join intent. */
export function adminInviteActionSettings(orgCode: string) {
  const origin = process.env.REACT_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const url = `${origin}/activate?intent=join_org&code=${encodeURIComponent(orgCode)}`;
  return { url, handleCodeInApp: true };
}

export function saveSignup(payload: SelfServePayload): void {
  try {
    localStorage.setItem(EMAIL_KEY, payload.email.trim().toLowerCase());
    localStorage.setItem(PAYLOAD_KEY, JSON.stringify(payload));
  } catch {
    /* storage may be unavailable (private mode) — caller still has the link */
  }
}

export function loadSignup(): { email: string; payload: SelfServePayload | null } {
  let email = '';
  let payload: SelfServePayload | null = null;
  try {
    email = localStorage.getItem(EMAIL_KEY) || '';
    const raw = localStorage.getItem(PAYLOAD_KEY);
    if (raw) payload = JSON.parse(raw) as SelfServePayload;
  } catch {
    /* ignore */
  }
  return { email, payload };
}

export function clearSignup(): void {
  try {
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(PAYLOAD_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Cross-browser signup draft — written at payment time (no auth required),
// readable from any browser after the user proves email ownership via the
// Firebase sign-in link. Fallback when localStorage is unavailable.
// ---------------------------------------------------------------------------
const DRAFT_COLLECTION = 'signupDrafts';

export async function saveSignupDraft(payload: SelfServePayload): Promise<void> {
  const email = payload.email.trim().toLowerCase();
  try {
    await setDoc(doc(db, DRAFT_COLLECTION, email), {
      ...payload,
      savedAt: serverTimestamp(),
    });
  } catch {
    /* non-fatal — localStorage is the primary store; Firestore draft is a fallback */
  }
}

export async function loadSignupDraft(email: string): Promise<SelfServePayload | null> {
  try {
    const snap = await getDoc(doc(db, DRAFT_COLLECTION, email.trim().toLowerCase()));
    if (snap.exists()) {
      const { savedAt: _s, ...payload } = snap.data() as SelfServePayload & { savedAt: unknown };
      return payload;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export async function clearSignupDraft(email: string): Promise<void> {
  try {
    await deleteDoc(doc(db, DRAFT_COLLECTION, email.trim().toLowerCase()));
  } catch {
    /* ignore */
  }
}

function planLimits(plan: PaidPlanId): { maxStudents: number; maxTeachers: number; maxParents: number } {
  switch (plan) {
    case 'premium':
      return { maxStudents: 500, maxTeachers: 25, maxParents: 1000 };
    case 'enterprise':
      return { maxStudents: 999999, maxTeachers: 999999, maxParents: 999999 };
    default:
      return { maxStudents: 500, maxTeachers: 25, maxParents: 1000 };
  }
}

/** Pilot length / first paid cycle, in days — purely for the mock expiry date.
 *  Trial mirrors the server: free for PILOT_DAYS, first charge one day later. */
function expiryDaysFor(p: SelfServePayload): number {
  if (p.intent === 'trial') return PILOT_DAYS + PILOT_BILL_DELAY_DAYS;
  if (p.period === 'year') return 365;
  if (p.period === 'term' || p.period === 'termly') return 120;
  return 30;
}

export interface ProvisionResult {
  ok: boolean;
  error?: string;
}

/**
 * Provision a self-serve org + its fleet-admin user, as the just-authenticated user.
 * Idempotent for the same admin (re-running the activation link won't error), but
 * refuses to hijack an org that already belongs to someone else.
 *
 * pendingActivation=true: called at payment time with anonymous auth — org is created
 * but marked pending until the user clicks the email link and sets a password.
 * pendingActivation=false (default): called at /activate after email link sign-in —
 * transfers ownership from the anonymous uid to the real email-authenticated uid.
 */
export async function provisionSelfServeOrg(user: User, payload: SelfServePayload, pendingActivation = false): Promise<ProvisionResult> {
  const code = (payload.code || '').trim();
  if (!code) return { ok: false, error: 'Missing organization code. Please start over from the pricing page.' };

  // Collision guard: a code that already exists and isn't ours can't be taken over.
  // Exception: a pending self-serve org whose adminEmail matches this user's email may
  // be claimed (the email-link user is taking over from the anonymous provisional uid).
  let existingData: Record<string, unknown> | null = null;
  try {
    const existing = await getDoc(doc(db, 'organizations', code));
    if (existing.exists()) {
      existingData = existing.data() as Record<string, unknown>;
      if (existingData.adminUid && existingData.adminUid !== user.uid) {
        const isPendingClaim =
          existingData.pendingActivation === true &&
          typeof existingData.adminEmail === 'string' &&
          existingData.adminEmail === (user.email || '').trim().toLowerCase();
        if (!isPendingClaim) {
          return { ok: false, error: 'That organization code is already taken. Contact PEAR support to recover access.' };
        }
        // Falls through — we'll overwrite adminUid with the real user uid below.
      }
    }
  } catch {
    /* read failure shouldn't block; the write rules are the real gate */
  }

  const productType = productTypeFor(payload);
  const limits = planLimits(payload.plan);
  const now = new Date();

  // Billing fields depend on the lifecycle stage:
  //  - pendingActivation (pre-payment): explicitly NOT active. The payments webhook
  //    (Admin SDK) flips these after charge.success — an unpaid org must never pass
  //    the subscription gate or survive as a free account.
  //  - claim with server billing already present: write NOTHING billing-related — the
  //    webhook's paid state (real expiry/plan/cadence) must not be clobbered by
  //    client-computed mock values.
  //  - claim in dev builds with no server billing: mock-activate so the local flow
  //    (no payments backend) stays usable end-to-end. Never in production.
  const hasServerBilling = !!(existingData && (existingData.billingQuote || existingData.lastPaymentRef));
  let billingFields: Record<string, unknown> = {};
  if (pendingActivation) {
    billingFields = {
      subscriptionPlan: payload.plan,
      subscriptionStatus: 'pending_payment',
      hasActiveSubscription: false,
      isActive: false,
      isPilot: payload.intent === 'trial',
      billingPeriod: payload.period,
    };
  } else if (!hasServerBilling && process.env.NODE_ENV !== 'production') {
    billingFields = {
      subscriptionPlan: payload.plan,
      subscriptionStatus: 'active',
      hasActiveSubscription: true,
      isActive: true,
      isPilot: payload.intent === 'trial',
      expirationDate: new Date(now.getTime() + expiryDaysFor(payload) * 24 * 60 * 60 * 1000),
      billingPeriod: payload.period,
    };
  }

  const orgDoc = {
    name: payload.orgName,
    code,
    type: 'school' as const,
    productType,
    address: '',
    phone: payload.phone || '',
    email: payload.email.trim().toLowerCase(),
    createdAt: now,
    updatedAt: now,
    studentCount: 0,
    teacherCount: 0,
    parentCount: 0,
    ...billingFields,
    ...limits,
    // Add-ons declared at signup (status 'default' until the tech team connects them).
    addons: payload.addons,
    // Curriculum (school products only) — drives year-group pickers + promotion.
    educationSystem: payload.product === 'lorry' ? null : (payload.educationSystem || 'CBC'),
    // School term calendar for term-boundary billing (null for lorry).
    termCalendar: payload.termCalendar ?? null,
    currentTermStartDate: payload.currentTermStartDate ?? null,
    currentTermEndDate: payload.currentTermEndDate ?? null,
    // Org home location (school/depot gate) + its pending flag (invariant: pending === !home).
    homeLocations: payload.homeLocations ?? null,
    locationSetupPending: !payload.homeLocations,
    // Sizing captured from onboarding.
    plannedChildren: payload.size,
    plannedBuses: payload.fleetCount,
    plannedParents: payload.parents,
    // Self-serve provenance + ownership (drives the create rule).
    selfServe: true,
    adminUid: user.uid,
    adminEmail: payload.email.trim().toLowerCase(),
    // The contact's own name, which the apps show instead of their email.
    adminName: (payload.contactName || '').trim() || null,
    adminEmails: [payload.email.trim().toLowerCase()],
    // Lifecycle: true = provisioned by anonymous auth at payment time, waiting for email
    // confirmation. False (default) = fully activated after email-link sign-in.
    pendingActivation,
  };

  try {
    await setDoc(doc(db, 'organizations', code), orgDoc, { merge: true });
  } catch (e) {
    return { ok: false, error: humanizeWriteError(e, 'organization') };
  }

  // Skip user doc for the anonymous provisional user — the real fleet-admin doc
  // is written when the email-link user claims the org at /activate.
  if (pendingActivation) return { ok: true };

  const userDoc = {
    uid: user.uid,
    email: user.email,
    displayName: payload.contactName || user.email?.split('@')[0] || '',
    role: 'fleet_admin' as const,
    schoolCode: code,
    institution: code,
    organizationCode: code,
    orgCode: code,
    profilePicUrl: null,
    createdAt: serverTimestamp(),
    isFleetAdmin: true,
    emailVerified: true,
    signupComplete: true,
    selfServe: true,
  };

  try {
    await setDoc(doc(db, 'users', user.uid), userDoc, { merge: true });
  } catch (e) {
    return { ok: false, error: humanizeWriteError(e, 'admin profile') };
  }

  return { ok: true };
}

/**
 * Accept an admin invite: create the fleet_admin user doc and register the email on the org.
 * Called at /activate when intent=join_org — the user has already signed in via email link
 * and set their password before this is called.
 */
export async function claimAdminInvite(user: User, orgCode: string): Promise<ProvisionResult> {
  const code = orgCode.trim();
  if (!code) return { ok: false, error: 'Missing organization code.' };
  if (!user.email) return { ok: false, error: 'Your account has no email address.' };

  const invitedEmail = user.email.trim().toLowerCase();

  // Verify this email was invited. An address the org already NAMES has accepted
  // already, on any org: Console Beta schools wrote adminEmails without
  // pendingAdminInvites, and since 2026-09-15 Login.tsx moves a one-time-code
  // signup out of pendingAdminInvites — its emailed invite, opened later, must
  // still finish rather than say "already used". The rules decide either way.
  // The name the console or the signup recorded for the primary admin.
  let orgAdminName = '';
  try {
    const orgSnap = await getDoc(doc(db, 'organizations', code));
    if (!orgSnap.exists()) {
      return { ok: false, error: 'Organization not found. The invite may have expired or been revoked.' };
    }
    const data = orgSnap.data() as Record<string, unknown>;
    const pending = ((data.pendingAdminInvites as string[] | undefined) || []).map((e) => String(e).toLowerCase());
    const admins = ((data.adminEmails as string[] | undefined) || []).map((e) => String(e).toLowerCase());
    const primary = String(data.adminEmail || '').trim().toLowerCase();
    const namedOnOrg = admins.includes(invitedEmail) || primary === invitedEmail;
    if (primary === invitedEmail) orgAdminName = String(data.adminName || '').trim();
    if (!pending.includes(invitedEmail) && !namedOnOrg) {
      return { ok: false, error: 'This invite was not found or has already been used.' };
    }
  } catch {
    // Read failure — proceed, Firestore rules are the real gate
  }

  const userDoc = {
    uid: user.uid,
    email: invitedEmail,
    // Their own name, as the console recorded it, before the email's first half.
    displayName: user.displayName || orgAdminName || invitedEmail.split('@')[0],
    role: 'fleet_admin' as const,
    schoolCode: code,
    institution: code,
    organizationCode: code,
    orgCode: code,
    profilePicUrl: null,
    createdAt: serverTimestamp(),
    isFleetAdmin: true,
    emailVerified: true,
    signupComplete: true,
    selfServe: true,
  };

  try {
    await setDoc(doc(db, 'users', user.uid), userDoc, { merge: true });
  } catch (e) {
    return { ok: false, error: humanizeWriteError(e, 'admin profile') };
  }

  // Update org: add to adminEmails, remove from pendingAdminInvites
  try {
    await updateDoc(doc(db, 'organizations', code), {
      adminEmails: arrayUnion(invitedEmail),
      pendingAdminInvites: arrayRemove(invitedEmail),
      updatedAt: new Date(),
    });
  } catch {
    // Non-fatal: user doc was created, they can still log in
  }

  return { ok: true };
}

function humanizeWriteError(e: unknown, what: string): string {
  const code = (e as { code?: string })?.code || '';
  if (code === 'permission-denied') {
    return `We couldn't create your ${what} (permission denied). If this keeps happening, contact PEAR support.`;
  }
  return (e as Error)?.message || `Could not create your ${what}. Please try again.`;
}
